import { useFileDialog } from "@mantine/hooks";
import {
  ActionIcon,
  Checkbox,
  Input,
  Text,
  UnstyledButton
} from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useEffect, useMemo, useState } from "react";
import { Controller, type UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdClose, MdOutlineCloudUpload, MdOutlineEdit } from "react-icons/md";

import { ProtectedImage } from "@/components";
import { httpClient } from "@/http";

import { editImageModalAtom } from "../../atoms/add-product-page-atom";
import type { AddProductFormSchema } from "../../hooks/schemas/use-add-product-form-schema";
import type { EditProductFormSchema } from "../../hooks/schemas/use-edit-product-form-schema";
type PictureItemProps = {
  pic: any;
  index: number;
  onSetThumbnail: (index: number) => void;
  onCrop: (index: number) => void;
  onRemove: (index: number) => void;
};

const PictureItem = ({
  pic,
  index,
  onSetThumbnail,
  onCrop,
  onRemove
}: PictureItemProps) => {
  const [objectUrl, setObjectUrl] = useState<string>();

  useEffect(() => {
    if (pic.file) {
      const url = URL.createObjectURL(pic.file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setObjectUrl(undefined);
      return undefined;
    }
  }, [pic.file]);

  const preview =
    objectUrl ||
    (pic.mediaPath && pic.mediaPath !== "-"
      ? `media/${pic.mediaPath}`
      : undefined);

  return (
    <div className="h-[158px] w-[158px] overflow-hidden relative rounded-lg bg-mischka-100 group shrink-0 border border-mischka-200">
      <div className="absolute top-2 left-2 z-10 flex items-center justify-center">
        <Checkbox
          checked={pic.type === "thumbnail"}
          onChange={() => onSetThumbnail(index)}
          classNames={{
            input: "cursor-pointer border border-solid border-noble-blue-500"
          }}
          title="Set as thumbnail"
        />
      </div>

      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <ActionIcon
          size="sm"
          className="rounded-full"
          onClick={() => onCrop(index)}
        >
          <MdOutlineEdit size={14} />
        </ActionIcon>
        <ActionIcon
          size="sm"
          className="rounded-full"
          onClick={() => onRemove(index)}
        >
          <MdClose size={14} />
        </ActionIcon>
      </div>

      {preview &&
        (preview.startsWith("blob:") ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <ProtectedImage
            src={preview}
            alt=""
            className="w-full h-full object-cover"
          />
        ))}
    </div>
  );
};

type PictureUploadProps = {
  formMethods: UseFormReturn<AddProductFormSchema | EditProductFormSchema>;
};

export const PicturesUpload: React.FC<PictureUploadProps> = ({
  formMethods
}) => {
  const { t } = useTranslation("product");
  const {
    control,
    setValue,
    formState: { errors }
  } = formMethods;

  const picturesArray = useWatch({ control, name: "pictures" });
  const pictures = picturesArray || [];

  const { editedPictureFile } = useAtomValue(editImageModalAtom);
  const setEditImageModal = useSetAtom(editImageModalAtom);
  const resetEditImageModal = useResetAtom(editImageModalAtom);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Sync edited file back to the array
  useEffect(() => {
    if (editingIndex !== null && editedPictureFile) {
      const newPics = [...pictures];
      if (newPics[editingIndex]) {
        newPics[editingIndex] = {
          ...newPics[editingIndex],
          file: editedPictureFile
        };
        setValue("pictures", newPics, {
          shouldDirty: true,
          shouldValidate: true
        });

        resetEditImageModal();
        setEditingIndex(null);
      }
    }
  }, [
    editedPictureFile,
    editingIndex,
    pictures,
    setValue,
    resetEditImageModal
  ]);

  // Reset atom on unmount
  useEffect(() => {
    return () => {
      resetEditImageModal();
    };
  }, [resetEditImageModal]);

  const { open, reset } = useFileDialog({
    accept: "image/jpeg, image/png",
    multiple: true,
    onChange: (files) => {
      if (files && files.length > 0) {
        const newPics = [...pictures];

        const hasThumbnail = newPics.some((p) => p.type === "thumbnail");
        const startIndex = newPics.length;

        for (let i = 0; i < files.length; i++) {
          const isFirstNew = i === 0;
          newPics.push({
            mediaPath: "-",
            type: !hasThumbnail && isFirstNew ? "thumbnail" : "image",
            file: files[i],
            uid: Math.random().toString(36).substring(7)
          });
        }

        setValue("pictures", newPics, {
          shouldDirty: true,
          shouldValidate: true
        });

        if (files.length === 1) {
          setEditingIndex(startIndex);
          setEditImageModal({
            visible: true,
            originalPictureFile: files[0],
            editedPictureFile: null
          });
        }
        reset();
      }
    }
  });

  const handleRemoveImage = (index: number) => {
    const newPics = [...pictures];
    const removed = newPics.splice(index, 1)[0];

    // If we removed the thumbnail and there are still pictures, set the first one as thumbnail
    if (removed.type === "thumbnail" && newPics.length > 0) {
      newPics[0].type = "thumbnail";
    }

    setValue("pictures", newPics, { shouldDirty: true, shouldValidate: true });
  };

  const handleSetThumbnail = (index: number) => {
    const newPics = pictures.map((pic: any, i: number) => ({
      ...pic,
      type: i === index ? "thumbnail" : "image"
    }));
    setValue("pictures", newPics, { shouldDirty: true, shouldValidate: true });
  };

  const handleCrop = async (index: number) => {
    const pic = pictures[index];
    let file = pic.file;

    if (!file && pic.mediaPath && pic.mediaPath !== "-") {
      try {
        const res = await httpClient.get(`media/${pic.mediaPath}`);
        if (!res.ok) throw new Error("Failed to fetch image");
        const blob = await res.blob();
        const filename = pic.mediaPath.split("/").pop() || "image.jpg";
        file = new File([blob], filename, { type: blob.type });
      } catch {
        notifications.show({
          variant: "error",
          message: "Failed to load image for cropping"
        });
        return;
      }
    }

    if (file) {
      setEditingIndex(index);
      setEditImageModal({
        visible: true,
        originalPictureFile: file,
        editedPictureFile: null
      });
    }
  };

  const errorMessage = useMemo(() => {
    if (!errors.pictures) return undefined;
    if ("message" in errors.pictures) {
      return errors.pictures.message as string;
    }
    if (Array.isArray(errors.pictures) && errors.pictures.length > 0) {
      const firstError = errors.pictures.find((e) => e != null);
      if (firstError && "mediaPath" in firstError)
        return firstError.mediaPath?.message as string;
      if (firstError && "type" in firstError)
        return firstError.type?.message as string;
    }
    return undefined;
  }, [errors.pictures]);

  return (
    <Controller
      control={control}
      name="pictures"
      render={() => (
        <Input.Wrapper error={errorMessage}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-[14px]">
              <UnstyledButton
                onClick={open}
                className={clsx(
                  "h-[158px] w-[158px] rounded-lg border border-dashed border-fjord-grey-200",
                  "flex flex-col justify-center items-center text-fjord-grey-200 shrink-0 bg-white"
                )}
              >
                <MdOutlineCloudUpload
                  className="text-noble-blue-500 mb-2"
                  size={32}
                />
                <Text
                  size="s"
                  className="text-noble-blue-500 font-medium text-center px-2"
                >
                  {t("addProductPage.form.labels.chooseFile")}
                </Text>
                <Text
                  size="xs"
                  className="text-fjord-grey-200 text-center px-2 mt-1"
                >
                  {t("addProductPage.form.labels.fileFormat")}
                </Text>
              </UnstyledButton>

              {pictures.map((pic: any, index: number) => (
                <PictureItem
                  key={pic.uid}
                  pic={pic}
                  index={index}
                  onSetThumbnail={handleSetThumbnail}
                  onCrop={handleCrop}
                  onRemove={handleRemoveImage}
                />
              ))}
            </div>

            {pictures.length > 0 && (
              <Text size="s" weight="bold" className="text-fjord-grey-500">
                Check the image you want to use as a thumbnail.
              </Text>
            )}
          </div>
        </Input.Wrapper>
      )}
    />
  );
};
